import React, { useState, useEffect, useRef } from 'react';
import { COLOR_FILTER } from 'ui/constants/colorFilter';
import BodySmall from '../Text/BodySmall';
import generateFilterNewCar from '../../../helpers/utils/generateFilterNewCar';
import Accordion from '../Accordion';
import DropUpArrow from '../Icons/DropUpArrow';
import DropDownArrow from '../Icons/DropDownArrow';
import { useRouter } from '../../../hooks/useCustomRouter';
import { Price, SpecList } from 'ui/interface';
import { usePathname, useSearchParams } from 'next/navigation';
import { useDispatch } from 'react-redux';
import eventClickNCL from '../../../helpers/emarsys/new-car-list/eventClickNCL';
import useImpressionNCL from '../../../hooks/emarsys/new-car-list/useImpressionNCL';
import { IBreadcrumbs } from '../../molecules/Breadcrumbs';

interface FilterModalContentProps {
  colorList: SpecList;
  fuelList: SpecList;
  transmisionList: SpecList;
  seatList: SpecList;
  minPrice: Price;
  maxPrice: Price;
  promotionList: SpecList;
  slideMin: number;
  slideMax: number;
  impressionData: {
    customerNo: string;
    title: string;
    carCategory: string;
    carModel: string;
    sectionName: string;
    filterValue?: {
      price?: string;
      search?: string;
      color?: string;
      fuel_type?: string;
      transmission?: string;
      capacity?: string;
      tag?: string;
      order_by?: string;
    };
  };
  feature?: string;
  breadcrumbItems?: IBreadcrumbs[];
}

export default function FilterModalContent({
  colorList,
  fuelList,
  transmisionList,
  seatList,
  minPrice,
  maxPrice,
  promotionList,
  slideMin,
  slideMax,
  impressionData,
  feature,
  breadcrumbItems,
}: FilterModalContentProps) {
  const impressionRef = useRef(null);
  const dispatch = useDispatch();
  const router = useRouter();
  const searchParams = useSearchParams();

  const pathname = usePathname();
  const filters = generateFilterNewCar(
    colorList,
    fuelList,
    transmisionList,
    seatList,
    minPrice,
    maxPrice,
    promotionList,
    slideMin,
    slideMax,
    feature,
    impressionData,
    breadcrumbItems
  );

  // Color
  const [colorListMobile, setColorListMobile] = useState([]);
  // Fuel
  const [fuelListMobile, setFuelListMobile] = useState([]);
  // Transmission
  const [transmissionListMobile, setTransmissionListMobile] = useState([]);
  // Seat
  const [seatMobile, setSeatMobile] = useState([]);
  // Tag
  const [TagMobile, setTagMobile] = useState([]);

  useEffect(() => {
    const existingQueryParams = searchParams.get('q');

    // Color
    const filteringColor = COLOR_FILTER?.filter((items) =>
      existingQueryParams?.includes(items?.enLabel)
    );

    // Fuel
    const filteringFuel = fuelList?.values
      ?.filter((items) => existingQueryParams?.includes(items?.name))
      ?.map((items) => items?.name);

    // Transmission
    const filteringTransmission = transmisionList?.values
      ?.filter((items) => existingQueryParams?.includes(items?.name))
      ?.map((items) => items?.name);

    // Seat
    const splitSeatList = existingQueryParams?.includes(':capacity:')
      ? (existingQueryParams as string)?.split(':capacity:')?.slice(1).join()
      : [];
    const filteringSeatList = seatList?.values
      ?.filter((items) => {
        return splitSeatList.toString().split(',').includes(items?.name);
      })
      ?.map((items) => items?.name);

    // Tag
    const filterinTagList = promotionList?.values
      ?.filter((items) => {
        const names = items?.name === 'TERLARIS' ? 'BESTSELLER' : items?.name;
        return existingQueryParams?.includes(names);
      })
      ?.map((items) => items?.name);

    // Color
    setColorListMobile(filteringColor);
    // Fuel
    setFuelListMobile(filteringFuel);
    // transmission
    setTransmissionListMobile(filteringTransmission);
    // Seat
    setSeatMobile(filteringSeatList);
    // Tag
    setTagMobile(filterinTagList);
  }, [searchParams]);

  const [hasNewCarFuelType, setHasNewCarFuelType] = useState(false);

  useEffect(() => {
    const qParam = searchParams.get('q');

    if (qParam) {
      const decodedParam = decodeURIComponent(qParam);
      setHasNewCarFuelType(decodedParam.includes(':newCarFuelType:'));
    } else {
      setHasNewCarFuelType(false);
    }
  }, [searchParams]);

  const handleResetFilter = () => {
    // Emarsys Impression
    eventClickNCL(
      dispatch,
      impressionData.customerNo,
      impressionData.title,
      impressionData.carCategory,
      impressionData.carModel,
      'filter',
      'reset filter',
      breadcrumbItems
    );

    router.push(`/${router?.query?.city ? '?city=' + router?.query?.city : ''}`);
    setColorListMobile([]);
    setFuelListMobile([]);
    setTransmissionListMobile([]);
    setSeatMobile([]);
    setTagMobile([]);
  };

  const handleDeleteFilterColor = (value) => {
    // Remove By item on State
    const removeColorFromList = colorListMobile.filter(
      (items) => items?.label !== value?.label && items.enLabel !== value?.enLabel
    );
    setColorListMobile(removeColorFromList);

    const selectedColorListValueTrue = colorList?.values.filter((items) => items.selected === true);
    const getValueByName = selectedColorListValueTrue.filter(
      (items) => items?.name.toUpperCase() === value.label.toUpperCase()
    )?.[0];

    // Change URL
    const newUrl = `${new URLSearchParams(
      getValueByName?.query?.query.value ? getValueByName?.query?.query.value : ''
    )
      .toString()
      .replace(/%3AbodyType%3A\w+/gi, '')}`;

    const priceFilter = searchParams.get('priceFilter');
    const defaultMin = searchParams.get('defaultMin');
    const defaultMax = searchParams.get('defaultMax');

    router.push(
      newUrl
        ? `${pathname}?q=${newUrl.replace('=', '')}${
            priceFilter
              ? `&priceFilter=${priceFilter}&defaultMin=${defaultMin}&defaultMax=${defaultMax}`
              : ''
          }`
        : ''
    );
  };

  const handleDeleteFilterFuel = (value) => {
    // Remove By item on State
    const removeFuelFromList = fuelListMobile.filter((items) => items !== value);
    setFuelListMobile(removeFuelFromList);

    const selectedFuelListValueTrue = fuelList?.values.filter((items) => items.selected === true);
    const getValueByName = selectedFuelListValueTrue.filter(
      (items) => items?.name.toUpperCase() === value.toUpperCase()
    )?.[0];

    // Change URL
    const newUrl = `${new URLSearchParams(
      getValueByName?.query?.query.value ? getValueByName?.query?.query.value : ''
    )
      .toString()
      .replace(/%3AbodyType%3A\w+/gi, '')}`;

    const priceFilter = searchParams.get('priceFilter');
    const defaultMin = searchParams.get('defaultMin');
    const defaultMax = searchParams.get('defaultMax');

    router.push(
      newUrl
        ? `${pathname}?q=${newUrl.replace('=', '')}${
            priceFilter
              ? `&priceFilter=${priceFilter}&defaultMin=${defaultMin}&defaultMax=${defaultMax}`
              : ''
          }`
        : ''
    );
  };

  const handleDeleteFilterTransmission = (value) => {
    // Remove By item on State
    const removeTransmissionFromList = transmissionListMobile.filter((items) => items !== value);
    setTransmissionListMobile(removeTransmissionFromList);

    const selectedTransmissionValueTrue = transmisionList?.values.filter(
      (items) => items.selected === true
    );
    const getValueByName = selectedTransmissionValueTrue.filter(
      (items) => items?.name.toUpperCase() === value.toUpperCase()
    )?.[0];

    // Change URL
    const newUrl = `${new URLSearchParams(
      getValueByName?.query?.query.value ? getValueByName?.query?.query.value : ''
    )
      .toString()
      .replace(/%3AbodyType%3A\w+/gi, '')}`;

    const priceFilter = searchParams.get('priceFilter');
    const defaultMin = searchParams.get('defaultMin');
    const defaultMax = searchParams.get('defaultMax');

    router.push(
      newUrl
        ? `${pathname}?q=${newUrl.replace('=', '')}${
            priceFilter
              ? `&priceFilter=${priceFilter}&defaultMin=${defaultMin}&defaultMax=${defaultMax}`
              : ''
          }`
        : ''
    );
  };

  const handleDeleteFilterSeatList = (value) => {
    // Remove By item on State
    const removeSeatFromList = seatMobile.filter((items) => items !== value);
    setTransmissionListMobile(removeSeatFromList);

    const selectedSeatValueTrue = seatList?.values.filter((items) => items.selected === true);
    const getValueByName = selectedSeatValueTrue.filter(
      (items) => items?.name.toUpperCase() === value.toUpperCase()
    )?.[0];

    // Change URL
    const newUrl = `${new URLSearchParams(
      getValueByName?.query?.query.value ? getValueByName?.query?.query.value : ''
    )
      .toString()
      .replace(/%3AbodyType%3A\w+/gi, '')}`;

    const priceFilter = searchParams.get('priceFilter');
    const defaultMin = searchParams.get('defaultMin');
    const defaultMax = searchParams.get('defaultMax');

    router.push(
      newUrl
        ? `${pathname}?q=${newUrl.replace('=', '')}${
            priceFilter
              ? `&priceFilter=${priceFilter}&defaultMin=${defaultMin}&defaultMax=${defaultMax}`
              : ''
          }`
        : ''
    );
  };

  const handleDeleteFilterTagList = (value) => {
    // Remove By item on State
    const removeTagFromList = TagMobile.filter((items) => items !== value);
    setTagMobile(removeTagFromList);

    const selectedTagValueTrue = promotionList?.values.filter((items) => items.selected === true);
    const getValueByName = selectedTagValueTrue.filter(
      (items) => items?.name.toUpperCase() === value.toUpperCase()
    )?.[0];

    // Change URL
    const newUrl = `${new URLSearchParams(
      getValueByName?.query?.query.value
        ? getValueByName?.query?.query.value.replace(/:bodyType:[a-zA-Z]+:/gi, '')
        : ''
    )
      .toString()
      .replace(/%3AbodyType%3A\w+/gi, '')}`;

    const priceFilter = searchParams.get('priceFilter');
    const defaultMin = searchParams.get('defaultMin');
    const defaultMax = searchParams.get('defaultMax');

    router.push(
      newUrl
        ? `${pathname}?q=${newUrl.replace('=', '')}${
            priceFilter
              ? `&priceFilter=${priceFilter}&defaultMin=${defaultMin}&defaultMax=${defaultMax}`
              : ''
          }`
        : ''
    );
  };

  // Emarsys Impression
  useImpressionNCL(
    dispatch,
    impressionRef,
    impressionData.title,
    impressionData.carCategory,
    impressionData.carModel,
    'filter',
    breadcrumbItems
  );

  return (
    <div className="text-reliableBlack w-full" ref={impressionRef}>
      {(colorListMobile?.length > 0 ||
        fuelListMobile?.length > 0 ||
        transmissionListMobile?.length > 0 ||
        seatMobile?.length > 0 ||
        TagMobile?.length > 0) && (
        <div className="flex justify-between mb-2 px-4 mt-4">
          <div className="flex flex-wrap flex-1 gap-2">
            {/* Warna */}
            {colorListMobile.length > 0 && (
              <div className="flex gap-2 mb-2">
                {colorListMobile?.map((value, index) => {
                  return (
                    <div
                      key={index}
                      className="flex items-center border gap-2 px-2 py-1 bg-reliableBlack10 rounded-full"
                    >
                      <BodySmall className="text-xs">
                        {COLOR_FILTER.filter((color) => color.label === value.label)[0].enLabel}
                      </BodySmall>

                      <BodySmall
                        className="text-[#918F8F] text-xs cursor-pointer"
                        onClick={() => handleDeleteFilterColor(value)}
                      >
                        x
                      </BodySmall>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Jenis Bahan Bakar */}
            {fuelListMobile?.length > 0 && hasNewCarFuelType === true && (
              <div className="flex  flex-wrap gap-2 mb-2">
                {fuelListMobile?.map((value, index) => {
                  return (
                    <div
                      key={index}
                      className="flex items-center border gap-2 px-2 py-1 bg-reliableBlack10 rounded-full"
                    >
                      <BodySmall className="text-xs">{value?.toUpperCase()}</BodySmall>
                      <BodySmall
                        className="text-[#918F8F] text-xs cursor-pointer"
                        onClick={() => handleDeleteFilterFuel(value)}
                      >
                        x
                      </BodySmall>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Transmission */}
            {transmissionListMobile?.length > 0 && (
              <div className="flex  flex-wrap mb-2 gap-2">
                {transmissionListMobile?.map((value, index) => {
                  return (
                    <div
                      key={index}
                      className="flex items-center border gap-2 px-2 py-1 bg-reliableBlack10 rounded-full"
                    >
                      <BodySmall className="text-xs">{value?.toUpperCase()}</BodySmall>
                      <BodySmall
                        className="text-[#918F8F] text-xs cursor-pointer"
                        onClick={() => handleDeleteFilterTransmission(value)}
                      >
                        x
                      </BodySmall>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Seat */}
            {seatMobile?.length > 0 && (
              <div className="flex  flex-wrap mb-2 gap-2">
                {seatMobile?.map((value, index) => {
                  return (
                    <div
                      key={index}
                      className="flex items-center border gap-2 px-2 py-1 bg-reliableBlack10 rounded-full"
                    >
                      <BodySmall className="text-xs">{value}</BodySmall>
                      <BodySmall
                        className="text-[#918F8F] text-xs cursor-pointer"
                        onClick={() => handleDeleteFilterSeatList(value)}
                      >
                        x
                      </BodySmall>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Tag */}
            {TagMobile?.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {TagMobile?.map((value, index) => {
                  return (
                    <div
                      key={index}
                      className="flex items-center border gap-2 px-2 py-1 bg-reliableBlack10 rounded-full"
                    >
                      <BodySmall className="text-xs">{value?.toUpperCase()}</BodySmall>
                      <BodySmall
                        className="text-[#918F8F] text-xs cursor-pointer"
                        onClick={() => handleDeleteFilterTagList(value)}
                      >
                        x
                      </BodySmall>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div className="flex justify-end mt-1.5 cursor-pointer" onClick={handleResetFilter}>
            <BodySmall className="text-xs font-bold text-supportiveRed">Reset Filter</BodySmall>
          </div>
        </div>
      )}

      {filters.map((filter, index) => {
        return (
          <Accordion
            key={index}
            classNameSummary={
              '-my-[18px] py-[18px] text-sm text-start font-medium text-reliableBlack cursor-pointer lg:text-base'
            }
            classTitleWrapper={
              'w-full flex justify-between items-center px-4 lg:px-0 font-semibold text-[16px] text-[#231F20]'
            }
            classNameChildrenWrapper={
              'text-start text-sm font-normal text-reliableBlack leading-relaxed lg:text-base lg:leading-relaxed px-4 lg:px-0'
            }
            index={index.toString()}
            title={filter.title}
            activeIcon={<DropUpArrow />}
            inactiveIcon={<DropDownArrow />}
          >
            {filter.content}
          </Accordion>
        );
      })}
    </div>
  );
}
